type BaseUrlResolver = () => string;

let baseUrlResolver: BaseUrlResolver = () => "";

/** Wire up the SDK with the host app's base-URL strategy (e.g. localhost proxy vs prod). */
export function configureApi(resolver: BaseUrlResolver): void {
  baseUrlResolver = resolver;
}

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public details?: unknown,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

async function handleResponse<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const body = await res.json().catch(() => ({ error: res.statusText }));
    throw new ApiError(res.status, (body as { error?: string }).error ?? "Request failed", body);
  }
  return res.json() as Promise<T>;
}

function jsonHeaders(): HeadersInit {
  return { "Content-Type": "application/json" };
}

export const api = {
  /** Escape hatch for verbs with bodies the helpers below don't cover (e.g. DELETE + JSON). */
  request<T>(path: string, init: { method: string; body?: unknown }): Promise<T> {
    return fetch(`${baseUrlResolver()}${path}`, {
      method: init.method,
      credentials: "include",
      headers: init.body !== undefined ? jsonHeaders() : undefined,
      body: init.body !== undefined ? JSON.stringify(init.body) : undefined,
    }).then(handleResponse<T>);
  },

  get<T>(path: string): Promise<T> {
    return fetch(`${baseUrlResolver()}${path}`, {
      credentials: "include",
    }).then(handleResponse<T>);
  },

  post<T>(path: string, body?: unknown): Promise<T> {
    return fetch(`${baseUrlResolver()}${path}`, {
      method: "POST",
      credentials: "include",
      headers: jsonHeaders(),
      body: body ? JSON.stringify(body) : undefined,
    }).then(handleResponse<T>);
  },

  put<T>(path: string, body?: unknown): Promise<T> {
    return fetch(`${baseUrlResolver()}${path}`, {
      method: "PUT",
      credentials: "include",
      headers: jsonHeaders(),
      body: body ? JSON.stringify(body) : undefined,
    }).then(handleResponse<T>);
  },

  patch<T>(path: string, body?: unknown): Promise<T> {
    return fetch(`${baseUrlResolver()}${path}`, {
      method: "PATCH",
      credentials: "include",
      headers: jsonHeaders(),
      body: body ? JSON.stringify(body) : undefined,
    }).then(handleResponse<T>);
  },

  delete<T>(path: string): Promise<T> {
    return fetch(`${baseUrlResolver()}${path}`, {
      method: "DELETE",
      credentials: "include",
    }).then(handleResponse<T>);
  },

  upload<T>(path: string, formData: FormData): Promise<T> {
    return fetch(`${baseUrlResolver()}${path}`, {
      method: "POST",
      credentials: "include",
      body: formData,
    }).then(handleResponse<T>);
  },
};
